import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { BillingAPI } from '../../../api';
import CardForm from '../../../components/braintree/CardForm';
import Alert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';
import BillingSummary from './BillingSummary';
import MatchingFees from './MatchingFees';
import PlanCardGroup from './PlanCardGroup';
import Link from '@material-ui/core/Link';
import { Link as RouterLink } from 'react-router-dom';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';


const defaultErrorMessage = 'We could not process this request.  Please try again later.';
const getErrorMessage = (response) => response.body && response.body.message ? response.body.message : defaultErrorMessage;

const useStyles = makeStyles({
  cardContainer: {
    margin: '1rem 0 1rem 0',
  },
  instructions: {
    margin: '1rem 0 1rem 0',
  },
});

const TermsChecked = ({termsAgreed, setTermsAgreed}) => {
  return (
    <FormControlLabel
      control={<Checkbox checked={termsAgreed} onChange={e=>setTermsAgreed(e.target.checked)} />}
      label={
        (
          <Typography variant="body2">
            I agree to Clickability&apos;s&nbsp;
            <Link component={RouterLink} to="/terms">
              <b>Terms and Conditions</b>
            </Link>
            &nbsp;&&nbsp;
            <Link component={RouterLink} to="/privacy">
              <b>Privacy Policy.</b>
            </Link>*
          </Typography>
        )
      }
    />
  );
};

TermsChecked.propTypes = {
  termsAgreed: PropTypes.bool.isRequired,
  setTermsAgreed: PropTypes.func.isRequired,
};

const BillingEnrollmentForm = ({code, onComplete}) => {
  const classes = useStyles();
  const [context, setContext] = React.useState();
  const [optionNumber, setOptionNumber] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState();
  const [termsAgreed, setTermsAgreed] = React.useState(false);

  const onConfirm = async ({billing_payment_method_nonce}) => {
    setBusy(true);
    setErrorMessage();
    try {
      await BillingAPI.setProviderBillingEnrolment(code, billing_payment_method_nonce, optionNumber);
      setBusy(false);
      onComplete();
    } catch (err) {
      setBusy(false);
      setErrorMessage(getErrorMessage(err));
    }
  };

  const renderOptions = () => {
    // construct [{id:id, description: [title, amount, period, note, info]}]
    const plans = context.options.map((o, i) => ({id: i, description: o.plan_description}));
    return (
      <React.Fragment>
        <Alert icon={false} severity="info">
          Please select a billing option.  Your card will be charged on the billing date shown below.
        </Alert>
        <PlanCardGroup plans={plans} planId={optionNumber} onChange={setOptionNumber}/>
      </React.Fragment>
    );
  };

  React.useEffect(() => {
    const getData = async() => {
      setErrorMessage();
      try {
        const { payload } = await BillingAPI.getProviderBillingEnrolment(code);
        setContext(payload);
      } catch (err) {
        setErrorMessage(getErrorMessage(err));
      }
    };

    if (code !== undefined) {
      getData();
    }
  }, [code]);

  if (!context) {
    if (errorMessage) {
      return (
        <Alert severity="error">
          {errorMessage}
        </Alert>
      );
    }
    return null;
  }

  return (
    <div>
      <Typography variant="h2" color="primary">
        Accreditation for {context.provider_name}
      </Typography>
      {(context.options.length > 1) && renderOptions()}
      <BillingSummary billing={{
        ...context,
        ...context.options[optionNumber],
      }}/>
      {context.matching_fees &&
        <React.Fragment>
          <Typography variant="h2" color="primary">
            Matching Fees
          </Typography>
          <MatchingFees matchingFees={context.matching_fees}/>
        </React.Fragment>
      }

      <Alert className={classes.instructions} icon={false} severity="info">Please enter credit card payment details to ensure your continued accreditation with Clickability.</Alert>
      <Typography variant="h2" color="primary">
        Credit Card Details
      </Typography>
      <div className={classes.cardContainer}>
        <CardForm
          onComplete={onConfirm}
          buttonLabel="Save Payment Method"
          providerUuid={context.provider_organisation_uuid}
          busy={busy}
          disabled={!termsAgreed}
          clientToken={context.client_token}
        >
          <TermsChecked termsAgreed={termsAgreed} setTermsAgreed={setTermsAgreed}/>
        </CardForm>
      </div>
      {errorMessage &&
        <Alert severity="error">
          {errorMessage}
        </Alert>
      }
    </div>
  );
};

BillingEnrollmentForm.propTypes = {
  code: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default BillingEnrollmentForm;
